import type { ReviewReasonEvidence } from './reviewReason.types';

export type AiDigitalFootprintEvidenceView = {
  primaryDataPoints: ReviewReasonEvidence[];
  collectedSignals: ReviewReasonEvidence[];
  issueStatement: string;
  gateRequirementNote: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function formatConfidence(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (value <= 1) return `${Math.round(value * 100)}%`;
  return `${Math.round(value)}%`;
}

function formatThreshold(value: unknown): string | null {
  const pct = formatConfidence(value);
  return pct ? `≥${pct}` : null;
}

function formatRealCompany(value: unknown): string | null {
  if (typeof value !== 'boolean') return null;
  return value ? 'Yes' : 'No';
}

function shortenUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return url.length > 48 ? `${url.slice(0, 47)}…` : url;
  }
}

function formatBranch(branch: Record<string, unknown> | null): string | null {
  if (!branch) return null;
  const name = readString(branch.name);
  const address = readString(branch.address);
  const phone = readString(branch.phone);
  const parts = [name, address, phone].filter(Boolean);
  if (!parts.length) return null;
  const joined = parts.join(' · ');
  return joined.length > 72 ? `${joined.slice(0, 71)}…` : joined;
}

function parseReasonFields(reason: string): {
  detectedEmployer: string | null;
  confidence: string | null;
  website: string | null;
  linkedIn: string | null;
  businessNumber: string | null;
  corporationId: string | null;
  sector: string | null;
  origin: string | null;
  phone: string | null;
  branch: string | null;
} {
  const detectedEmployer =
    reason.match(/confidence\s+[\d.]+\):\s*([^.]+)\./i)?.[1]?.trim() ??
    reason.match(/matched\s+([^.]+)\s+with/i)?.[1]?.trim() ??
    null;
  return {
    detectedEmployer,
    confidence: reason.match(/confidence\s+(\d+(?:\.\d+)?)/i)?.[1] ?? null,
    website: reason.match(/Website:\s*(\S+)/i)?.[1] ?? null,
    linkedIn: reason.match(/LinkedIn:\s*(\S+)/i)?.[1] ?? null,
    businessNumber: reason.match(/BN:\s*(\S+)/i)?.[1] ?? null,
    corporationId: reason.match(/Corp ID:\s*(\S+)/i)?.[1] ?? null,
    sector: reason.match(/Sector:\s*([^.]+)\./i)?.[1]?.trim() ?? null,
    origin: reason.match(/Origin:\s*([^.]+)\./i)?.[1]?.trim() ?? null,
    phone: reason.match(/Phone:\s*([^.]+)\./i)?.[1]?.trim() ?? null,
    branch: reason.match(/Branch:\s*([^.]+)\./i)?.[1]?.trim() ?? null,
  };
}

function pushPoint(
  rows: ReviewReasonEvidence[],
  label: string,
  value: string | null | undefined,
  tone: ReviewReasonEvidence['tone'],
) {
  if (value) rows.push({ label, value, tone });
}

function listPublicIdentitySignals(input: {
  website: string | null;
  linkedIn: string | null;
  businessNumber: string | null;
}): string[] {
  const signals: string[] = [];
  if (input.website) signals.push('official website');
  if (input.linkedIn) signals.push('LinkedIn company page');
  if (input.businessNumber) signals.push('Canadian business number');
  return signals;
}

/** Rich adjudicator view for LEGITIMACY / AI_DIGITAL_FOOTPRINT. */
export function buildAiDigitalFootprintEvidenceView(input: {
  evidence: Record<string, unknown> | null | undefined;
  reason: string;
  bucket: string;
  declaredEmployer?: string | null;
}): AiDigitalFootprintEvidenceView {
  const ai = asRecord(input.evidence?.aiDigitalFootprint) ?? asRecord(input.evidence);
  const parsed = parseReasonFields(input.reason);
  const branch = asRecord(ai?.matchedBranch);

  const detectedEmployer =
    readString(ai?.detectedEmployer) ?? parsed.detectedEmployer ?? input.declaredEmployer ?? null;
  const confidence =
    formatConfidence(ai?.overallConfidence) ??
    (parsed.confidence
      ? formatConfidence(Number.parseFloat(parsed.confidence))
      : null);
  const threshold = formatThreshold(ai?.confidenceThreshold) ?? '≥90%';
  const realCompany = formatRealCompany(ai?.isRealCompany);

  const website = readString(ai?.officialWebsite) ?? parsed.website;
  const linkedIn = readString(ai?.linkedInUrl) ?? parsed.linkedIn;
  const businessNumber = readString(ai?.businessNumber) ?? parsed.businessNumber;
  const corporationId = readString(ai?.corporationId) ?? parsed.corporationId;
  const sector = readString(ai?.sector) ?? parsed.sector;
  const origin = readString(ai?.companyOriginCountry) ?? parsed.origin;
  const phone = readString(ai?.phone) ?? parsed.phone;
  const branchLabel = formatBranch(branch) ?? parsed.branch;

  const primaryDataPoints: ReviewReasonEvidence[] = [];
  pushPoint(primaryDataPoints, 'AI match', detectedEmployer, 'aiInsight');
  pushPoint(primaryDataPoints, 'Confidence', confidence, 'reviews');
  pushPoint(primaryDataPoints, 'Pass threshold', threshold, 'threshold');
  pushPoint(primaryDataPoints, 'Real company', realCompany, 'places');

  const collectedSignals: ReviewReasonEvidence[] = [];
  pushPoint(
    collectedSignals,
    'Official website',
    website ? shortenUrl(website) : null,
    'domain',
  );
  pushPoint(
    collectedSignals,
    'LinkedIn',
    linkedIn ? shortenUrl(linkedIn) : null,
    'aiInsight',
  );
  pushPoint(collectedSignals, 'Business number', businessNumber, 'registry');
  pushPoint(collectedSignals, 'Corporation ID', corporationId, 'registry');
  pushPoint(collectedSignals, 'Sector', sector, 'vision');
  pushPoint(collectedSignals, 'Origin country', origin, 'satellite');
  pushPoint(collectedSignals, 'Phone', phone, 'places');
  pushPoint(collectedSignals, 'Matched branch', branchLabel, 'workplace');

  const identitySignals = listPublicIdentitySignals({ website, linkedIn, businessNumber });
  const gateRequirementNote =
    'Gate requires at least one collected public identity signal: official website, LinkedIn company page, or valid Canadian business number.';

  let issueStatement: string;
  if (input.bucket.trim().toLowerCase() === 'pass') {
    if (detectedEmployer && confidence && identitySignals.length) {
      issueStatement = `Prior AI digital analysis matched ${detectedEmployer} at ${confidence} confidence. Collected ${identitySignals.join(', ')}.`;
    } else if (detectedEmployer && confidence) {
      issueStatement = `Prior AI digital analysis matched ${detectedEmployer} at ${confidence} confidence.`;
    } else if (identitySignals.length) {
      issueStatement = `Collected public identity signals: ${identitySignals.join(', ')}.`;
    } else {
      issueStatement = parsed.detectedEmployer
        ? `AI public footprint matched ${parsed.detectedEmployer}.`
        : 'AI public footprint analysis completed with collected digital signals.';
    }
  } else {
    issueStatement =
      identitySignals.length > 0
        ? `Digital analysis found ${identitySignals.join(', ')}, but the checkpoint did not pass — review the collected signals.`
        : 'Insufficient public identity signals were collected from the prior AI digital analysis.';
  }

  return {
    primaryDataPoints,
    collectedSignals,
    issueStatement,
    gateRequirementNote,
  };
}
